import intl from "react-intl-universal";
import {
  CurrencyIcon,
  CurrencyIconPair,
  CurrencyIconSlot,
} from "../../../../Components/Atoms/CurrencyIcon";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
  GridHistoryStatusActionsHeader,
} from "../../../../Components/Molecules/GridHistory";
import {
  formatBrl,
  formatDepositHistoryTitle,
  formatPurchaseHistoryTitle,
  formatSaleHistoryTitle,
  formatStable,
  formatSwapHistoryTitle,
  formatTransferHistoryTitle,
  formatWithdrawalDestination,
  formatWithdrawalHistoryAmount,
  formatWithdrawalHistoryFeeDetail,
  formatWithdrawalHistoryTitle,
  getHistoryKindMeta,
  getStablesStatusLabel,
} from "../constants";
import { formatQuotePrice } from "../stablesQuoteDisplay";

export const StablesHistoryStatusBadge = ({ status }) => {
  const value = String(status || "").toLowerCase();
  const isPending =
    value.includes("process") || value === "pending" || value.includes("pendent");
  const isFailed = value.includes("falh") || value === "failed" || value.includes("fail");

  const label = isFailed
    ? intl.get("baas.stablesWallet.operations.status.failed")
    : getStablesStatusLabel(status);

  const toneClass = isFailed
    ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
    : isPending
      ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";

  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${toneClass}`}>
      {label}
    </span>
  );
};

export const StablesHistoryTypeBadge = ({ historyKind }) => {
  const meta = getHistoryKindMeta()[historyKind];
  if (!meta) return null;

  return (
    <span
      className={`inline-flex w-[5.5rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badgeClass}`}
    >
      {meta.label}
    </span>
  );
};

export const StablesHistoryHeaderRow = () => (
  <GridHistoryHeader colSpan={4}>
    <span>{intl.get("grid.transaction")}</span>
    <span>{intl.get("grid.date")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>{intl.get("grid.value")}</span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("grid.status")} />
  </GridHistoryHeader>
);

const StatusActionsCell = ({ onShowDetail, item, status, showDetail }) => {
  const statusBadge = <StablesHistoryStatusBadge status={status} />;

  if (!showDetail || !onShowDetail) {
    return (
      <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
        <p className="mb-px w-full text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
          {intl.get("grid.status")}
        </p>
        {statusBadge}
      </div>
    );
  }

  return (
    <GridHistoryStatusActionsCell
      statusLabel={intl.get("grid.status")}
      statusBadge={statusBadge}
      onShowDetail={onShowDetail}
      item={item}
    />
  );
};

const quoteRateDetail = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return null;
  return intl.get("stables.quoteRate", { rate: formatQuotePrice(n) });
};

const getGeneralRowContent = (item) => {
  switch (item.historyKind) {
    case "purchases":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatPurchaseHistoryTitle(item),
        date: item.date,
        amount: formatBrl(item.amountBrl),
        detail: quoteRateDetail(item.rate),
      };
    case "sales":
      return {
        icon: <CurrencyIcon asset={item.from} />,
        title: formatSaleHistoryTitle(item),
        date: item.date,
        amount: formatBrl(item.amountTo),
        detail: quoteRateDetail(item.rate),
      };
    case "swaps":
      return {
        icon: <CurrencyIconPair from={item.from} to={item.to} />,
        title: formatSwapHistoryTitle(item),
        date: item.date,
        amount: formatStable(item.amountTo, item.to),
        detail: intl.get("stables.sentAmount", {
          amount: formatStable(item.amountFrom, item.from),
        }),
      };
    case "withdrawals":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatWithdrawalHistoryTitle(item),
        date: item.date,
        subtitle: formatWithdrawalDestination(item.destination),
        amount: formatWithdrawalHistoryAmount(item),
        detail: formatWithdrawalHistoryFeeDetail(item),
      };
    case "deposits":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatDepositHistoryTitle(item),
        date: item.date,
        amount: formatStable(item.amount, item.asset),
        detail: item.reference,
      };
    case "transfers":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatTransferHistoryTitle(item),
        date: item.date,
        subtitle: item.destination || item.reference,
        amount: formatStable(item.amount, item.asset),
        detail: item.reference,
      };
    default:
      return null;
  }
};

const GeneralHistoryRows = ({ items, onShowDetail, showDetail }) =>
  items.map((item) => {
    const content = getGeneralRowContent(item);
    if (!content) return null;

    return (
      <GridHistoryRow key={`${item.historyKind}-${item.id}`} colSpan={4}>
        <GridHistoryCell label={intl.get("grid.transaction")}>
          <div className="flex min-w-0 items-center gap-3">
            <CurrencyIconSlot>{content.icon}</CurrencyIconSlot>
            <StablesHistoryTypeBadge historyKind={item.historyKind} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {content.title}
              </p>
              {content.subtitle && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{content.subtitle}</p>
              )}
            </div>
          </div>
        </GridHistoryCell>
        <GridHistoryCell label={intl.get("grid.date")}>
          <p className="text-xs text-gray-500 dark:text-gray-400">{content.date}</p>
        </GridHistoryCell>
        <GridHistoryCell label={intl.get("grid.value")} align="right">
          {content.amount ? (
            <>
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {content.amount}
              </p>
              {content.detail && (
                <p className="text-[11px] text-gray-400">{content.detail}</p>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </GridHistoryCell>
        <StatusActionsCell
          onShowDetail={onShowDetail}
          item={item}
          status={item.status}
          showDetail={showDetail}
        />
      </GridHistoryRow>
    );
  });

export const StablesGeneralHistoryGrid = ({
  items = [],
  onShowDetail,
  showDetail = true,
  className = "",
}) => (
  <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4} className={className}>
    <StablesHistoryHeaderRow />
    <GeneralHistoryRows items={items} onShowDetail={onShowDetail} showDetail={showDetail} />
  </GridHistoryContainer>
);
